import { Link } from "react-router-dom";
import { Reveal, MaskIn, MaskLine, SectionHead, ParallaxImg } from "@/components/motion";
import { LogoMark } from "@/components/LogoMark";

const SERVICES = [
    {
        n: "01",
        t: "Business strategy and organisational development",
        img: "https://images.unsplash.com/photo-1528819622765-d6bcf132f793?crop=entropy&cs=srgb&fm=jpg&q=85",
        imgAlt: "Chess pieces on a board — strategy in play",
        d: "Helping organisations develop clear strategies for growth, transformation, competitiveness and long-term resilience.",
        items: [
            "Strategic planning",
            "Performance improvement",
            "Business transformation",
            "Organisational assessment",
            "Leadership and management advisory",
        ],
    },
    {
        n: "02",
        t: "Sustainability",
        img: "https://images.unsplash.com/photo-1598959237790-1590cc2776a6?crop=entropy&cs=srgb&fm=jpg&q=85",
        imgAlt: "Sustainable architecture with green planting",
        d: "Sustainability is increasingly a strategic, regulatory, financial and operational issue. ELCAYIL helps organisations understand sustainability expectations and integrate environmental, social and governance considerations into strategy and decision-making.",
        items: [
            "ESG strategy development",
            "Sustainability assessments",
            "ESG governance",
            "Sustainability reporting readiness",
            "Climate and transition considerations",
            "Stakeholder engagement",
            "Sustainable business practices",
            "ESG policy development",
        ],
    },
    {
        n: "03",
        t: "Governance and compliance",
        img: "https://images.unsplash.com/photo-1768617154318-b2caa1e97a60?crop=entropy&cs=srgb&fm=jpg&q=85",
        imgAlt: "Neoclassical institutional building columns",
        d: "Good governance provides the foundation for sustainable organisational performance. We help organisations strengthen governance structures, understand regulatory expectations, identify risks and establish appropriate policies and internal processes.",
        items: [
            "Governance reviews",
            "Regulatory and compliance advisory",
            "Policy development",
            "Risk assessment",
            "Internal governance frameworks",
            "Board and management advisory",
            "Organisational accountability structures",
        ],
    },
    {
        n: "04",
        t: "Organisational performance",
        img: "https://images.unsplash.com/photo-1636570166141-3f220dc60e25?crop=entropy&cs=srgb&fm=jpg&q=85",
        imgAlt: "Executive overlooking the city skyline",
        d: "Strong strategies require effective execution. We support organisations in improving processes, leadership practices, organisational structures and performance systems, helping them become more effective, accountable, responsive and results-driven.",
        items: [],
    },
    {
        n: "05",
        t: "Research and advisory",
        img: "https://images.unsplash.com/photo-1758876020343-c8c2add9d527?crop=entropy&cs=srgb&fm=jpg&q=85",
        imgAlt: "Advisor reviewing documents at a desk",
        d: "Complex business and policy decisions require credible evidence. ELCAYIL provides research and advisory services that help organisations understand emerging trends, regulatory developments, sustainability challenges and strategic opportunities.",
        items: [
            "Policy research",
            "Regulatory research",
            "Industry analysis",
            "Sustainability research",
            "Strategic reports",
            "Evidence-based advisory",
            "Executive briefing papers",
        ],
    },
    {
        n: "06",
        t: "Training and capacity building",
        img: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85",
        imgAlt: "Training workshop presentation",
        d: "Knowledge becomes valuable when organisations can apply it. ELCAYIL provides professional training and capacity-building programmes designed around organisational needs.",
        items: [
            "Leadership",
            "ESG",
            "Sustainability",
            "Governance",
            "Compliance",
            "Strategy",
            "Organisational performance",
        ],
    },
];

export default function Services() {
    return (
        <div>
            <section
                className="mx-auto max-w-7xl px-6 pt-32 md:px-12 md:pt-44"
                data-testid="services-hero"
            >
                <Reveal y={16}>
                    <p className="eyebrow text-ink/50">Services</p>
                </Reveal>
                <h1 className="mt-8 max-w-5xl font-serif-d text-4xl leading-[1.1] sm:text-5xl md:text-7xl">
                    <MaskLine delay={0.2}>
                        Advice with research depth,
                    </MaskLine>
                    <MaskLine delay={0.32}>
                        built for{" "}
                        <em className="italic text-brand">implementation</em>.
                    </MaskLine>
                </h1>
                <Reveal delay={0.2} className="mt-10 max-w-2xl">
                    <p className="text-[15px] leading-relaxed text-ink/60">
                        ELCAYIL CONSULTING LTD. provides strategic advisory,
                        sustainability, governance, compliance and
                        organisational performance solutions designed to help
                        businesses navigate complexity, strengthen
                        decision-making and achieve measurable results.
                    </p>
                </Reveal>
            </section>

            {SERVICES.map((s, i) => (
                <section
                    key={s.n}
                    className="border-t border-black/10"
                    data-testid={`services-section-${s.n}`}
                >
                    <div className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
                        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
                            <div className="lg:col-span-5">
                                <div className="lg:sticky lg:top-32">
                                    <Reveal>
                                        <span className="font-mono2 text-sm text-brand">
                                            {s.n}
                                        </span>
                                        <h2 className="mt-4 max-w-md font-serif-d text-3xl leading-tight md:text-5xl">
                                            {s.t}
                                        </h2>
                                    </Reveal>
                                    <Reveal
                                        delay={0.15}
                                        className="group mt-10 hidden lg:block"
                                    >
                                        <ParallaxImg
                                            src={s.img}
                                            alt={s.imgAlt}
                                            className="aspect-[4/3] w-full"
                                        />
                                    </Reveal>
                                </div>
                            </div>
                            <div className="lg:col-span-6 lg:col-start-7">
                                <Reveal delay={0.1}>
                                    <p className="text-[15px] leading-relaxed text-ink/60">
                                        {s.d}
                                    </p>
                                </Reveal>
                                {s.items.length > 0 && (
                                    <div className="mt-10">
                                        {s.items.map((it, j) => (
                                            <Reveal
                                                key={it}
                                                delay={j * 0.04}
                                                y={16}
                                            >
                                                <div
                                                    className="group flex items-center justify-between border-t border-black/10 py-4"
                                                    data-testid={`service-${s.n}-item-${j + 1}`}
                                                >
                                                    <span className="row-bg text-[15px] transition-colors duration-300 group-hover:text-brand md:text-base">
                                                        {it}
                                                    </span>
                                                    <span className="text-ink/20">
                                                        +
                                                    </span>
                                                </div>
                                            </Reveal>
                                        ))}
                                        <div className="border-t border-black/10" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            ))}

            <section className="bg-brand text-white" data-testid="services-cta">
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                    <Reveal>
                        <LogoMark variant="white" className="h-12 w-auto" />
                    </Reveal>
                    <MaskIn className="max-w-4xl">
                        <h2 className="font-serif-d text-4xl leading-tight md:text-6xl">
                            Not sure where your organisation needs to begin?
                        </h2>
                    </MaskIn>
                    <Reveal
                        delay={0.2}
                        className="mt-10 flex flex-wrap items-center gap-6"
                    >
                        <Link
                            to="/contact"
                            data-testid="services-cta-contact"
                            className="rounded-full bg-white px-8 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-brandsoft"
                        >
                            Start a Conversation
                        </Link>
                        <p className="text-sm text-white/60">
                            We would be pleased to discuss how ELCAYIL can
                            support you.
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
